import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, Phone, MapPin, Newspaper } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";
const PHONE = "+1 (470) 800-0081";
const PHONE_HREF = "+14708000081";
const EMAIL = "contact@heallexa.com";

const META_TITLE = brandedTitle("Contact Us");
const META_DESC = safeDesc(
  "Get in touch with the Heallexa team. Call +1 (470) 800-0081 or email contact@heallexa.com — we reply within 1–2 business days."
);

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: `${SITE}/contact` },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/contact` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "@id": `${SITE}/contact#webpage`,
          url: `${SITE}/contact`,
          name: META_TITLE,
          description: META_DESC,
          isPartOf: { "@id": `${SITE}/#website` },
          about: { "@id": `${SITE}/#organization` },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="mx-auto max-w-[1000px] px-6 pt-32 md:pt-40 pb-20">
        <div className="text-center">
          <h1 className="text-[2.6rem] md:text-[3.4rem] font-medium tracking-[-0.03em] leading-[1.05]">
            Contact Us
          </h1>
          <p className="mx-auto mt-4 max-w-[600px] text-[16px] leading-relaxed text-muted-foreground">
            Have a question about Heallexa, your practice profile, or our Premium Plan? We&apos;re
            here to help.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          <Card icon={Phone} label="Phone" value={PHONE} href={`tel:${PHONE_HREF}`}>
            Mon – Fri, 9 AM – 6 PM CT
          </Card>
          <Card icon={Mail} label="General inquiries" value={EMAIL} href={`mailto:${EMAIL}`}>
            Response within 1–2 business days
          </Card>
          <Card icon={Newspaper} label="Press &amp; media" value={EMAIL} href={`mailto:${EMAIL}`}>
            For journalists and media requests
          </Card>
        </div>

        <div className="mt-4 card-surface-flat flex gap-4 p-6">
          <span className="grid h-9 w-9 shrink-0 place-content-center rounded-lg bg-primary/10 text-primary">
            <MapPin className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="space-y-0.5">
            <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Office</p>
            <p className="text-[15px] font-medium">Heallexa Inc.</p>
            <p className="text-[14px] leading-relaxed text-muted-foreground">
              2351 W Northwest Hwy, Suite 1234
              <br />
              Dallas, TX 75220, United States
            </p>
          </div>
        </div>

        <div className="mt-10 border-t border-border/60 pt-8 text-center text-[13px] leading-relaxed text-muted-foreground space-y-2">
          <p>Business hours: Monday through Friday, 9:00 AM – 6:00 PM Central Time.</p>
          <p>
            Heallexa is a healthcare provider directory, not a healthcare provider. Contacting us
            does not create a provider-patient relationship, and we can&apos;t give medical advice,
            diagnosis, or treatment. In a medical emergency, call 911. See our{" "}
            <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}

function Card({
  icon: Icon,
  label,
  value,
  href,
  children,
}: {
  icon: typeof Phone;
  label: string;
  value: string;
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} className="card-surface-flat hover-lift group block p-6 text-center">
      <span className="mx-auto grid h-10 w-10 place-content-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <p className="mt-4 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-[15px] font-medium break-words group-hover:text-primary transition-colors">
        {value}
      </p>
      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{children}</p>
    </a>
  );
}
